import React from "react";
import {
  Box,
  IconButton,
  Typography,
  accordionSummaryClasses,
  buttonBaseClasses,
  typographyClasses
} from "@mui/material";
import I18n from "i18next";
import MzTooltip from "@markaaz/ui/MzTooltip";
import { MzAccordion, MzAccordionDetails, MzAccordionSummary } from "@markaaz/ui/MzAccordionControls";
import MzIcon from "mz/MzIcon";
import MarkaazMatchAccordion from "./MarkaazMatchAccordion";
import MarkaazMatchFoundDetails from "./MarkaazMatchFoundDetails";

const sx = {
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    borderRadius: "8px",
    "& .Mui-expanded .match-found-accordion": {
      marginBottom: "32px"
    },
    "& .MuiCollapse-root": {
      marginBottom: "16px"
    }
  },
  header: {
    display: "flex",
    width: "652px",
    height: "44px",
    padding: "4px 16px 4px 16px",
    alignItems: "center",
    borderRadius: "8px 8px 0px 0px",
    justifyContent: "space-between"
  },
  icon: {
    height: "24px",
    width: "25px"
  },
  markaazMatch: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
  },
  accordion: {
    [`&.${buttonBaseClasses.root}.${accordionSummaryClasses.root}`]: {
      backgroundColor: (theme) => `${theme.palette.acqua.c110}`,
      borderRadius: 2
    },
    [`& .${accordionSummaryClasses.content} .${typographyClasses.root}`]: {
      color: (theme) => theme.palette.common.white
    },
    [`&.${buttonBaseClasses.root}.${accordionSummaryClasses.root}.Mui-expanded`]: {
      borderRadius: "8px 8px 0px 0px"
    },
    svg: {
      color: (theme) => theme.palette.common.white,
      paddingRight: "16px"
    }
  },
  downloadBtn: {
    mt: 5
  },
  copyIcon: {
    padding: "0px 0px 0px 8px"
  }
};

export default function MarkaazMatchFound({
  index,
  matchFoundTooltipData,
  overviewData,
  firmographicsData,
  healthData,
  complianceData,
  hierarchyData,
  diversityData,
  nameMatchingData,
  contractTermsdata,
  retryData,
  onRetry,
  onCopyClick
}) {
  const componentData = [
    {
      condition: true,
      name: "Company Match Overview",
      data: overviewData?.data,
      message: overviewData?.message
    },
    {
      condition: contractTermsdata.bizFirmograhicEnabled,
      name: "Firmographic",
      data: firmographicsData?.data,
      message: firmographicsData?.message
    },
    {
      condition: contractTermsdata.bizHealthEnabled,
      name: "Business Health",
      data: healthData?.data,
      message: healthData?.message
    },
    {
      condition: contractTermsdata.bizComplianceEnabled,
      name: "Compliance",
      data: complianceData?.data,
      message: complianceData?.message
    },
    {
      condition: contractTermsdata.bizHeirarchyEnabled,
      name: "Company Hierarchy",
      data: hierarchyData?.data,
      message: hierarchyData?.message
    },
    {
      condition: contractTermsdata.bizDiversityEnabled,
      name: "Diversity",
      data: diversityData?.data,
      message: diversityData?.message
    },
    {
      condition: contractTermsdata.officerNameMatchingEnabled,
      name: "Name Matching",
      data: nameMatchingData?.data,
      message: nameMatchingData?.message
    }
  ];

  return (
    <Box sx={sx.root}>
      <Box>
        <MzAccordion defaultExpanded={index === 0}>
          <MzAccordionSummary className="match-found-accordion" sx={sx.accordion}>
            <Box sx={sx.header}>
              <Box sx={{ display: "flex", flexDirection: "row" }} gap="16px">
                <MzIcon name="IconAlert" sx={sx.icon} />
                <Typography variant="h6">
                  {index === 0 ? "Best Match:" : `Match ${index + 1}:`}
                  {overviewData?.data?.["Company Legal Name:"].length > 13
                    ? overviewData?.data?.["Company Legal Name:"].substring(0, 13) + "..."
                    : overviewData?.data?.["Company Legal Name:"]}
                </Typography>
              </Box>
              {overviewData?.data?.["Markaaz ID:"] && (
                <Box sx={sx.markaazMatch}>
                  <Box height="20px" marginBottom="4px">
                    <Typography variant="body2M">
                      {I18n.t("BusinessLookup.markaazId")} {overviewData?.data?.["Markaaz ID:"]}
                    </Typography>
                    <MzTooltip title="Copy Markaaz ID" placement="top">
                      <IconButton
                        sx={sx.copyIcon}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigator.clipboard.writeText(overviewData?.data?.["Markaaz ID:"]);
                        }}
                      >
                        <MzIcon name="CopyIcon" sx={sx.icon} />
                      </IconButton>
                    </MzTooltip>
                  </Box>
                  <Typography variant="body2M" color="common.white">
                    {I18n.t("BusinessLookup.matchConfidenceScore")}{" "}
                    {overviewData?.data?.["Markaaz Match Confidence Score:"]}
                  </Typography>
                </Box>
              )}
            </Box>
          </MzAccordionSummary>
          {componentData.map(
            (item, index) =>
              item.condition && (
                <>
                  <MzAccordionDetails>
                    <MarkaazMatchAccordion
                      key={index}
                      name={item.name}
                      retry={item.retry}
                      hasData={!!item.data}
                      onRetry={onRetry}
                      onCopyClick={() => onCopyClick(item.name, item.data)}
                    >
                      {item.data && (
                        <MarkaazMatchFoundDetails data={item.data} matchFoundTooltipData={matchFoundTooltipData} />
                      )}
                      {!item.data && <Box marginLeft="30px">{item.message}</Box>}
                    </MarkaazMatchAccordion>
                  </MzAccordionDetails>
                </>
              )
          )}
        </MzAccordion>
      </Box>
    </Box>
  );
}
